import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import UploadZone from '../components/UploadZone';
import ResultPanel from '../components/ResultPanel';
import EditorPanel from '../components/EditorPanel';
import MergeWordCard from '../components/MergeWordCard';
import MergePdfCard from '../components/MergePdfCard';
import MergeImagesPdfCard from '../components/MergeImagesPdfCard';
import PptxToPdfCard from '../components/PptxToPdfCard';
import PdfTableBoxCard from '../components/PdfTableBoxCard';
import SplitCard from '../components/SplitCard';
import CompressCard from '../components/CompressCard';
import RtlPdfWordCard from '../components/RtlPdfWordCard';
import SmartWhiteboard from '../components/SmartWhiteboard';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_API_URL || '';

const MODES = [
  { id: 'ltw',         icon: 'ArrowLeftRight', accentColor: '#e94560', inputFmt: 'TEX',  outputFmt: 'DOCX', bidir: true,  features: ['thesis','bidirectional','ar-fa-en','math-safe','rtl','per-run'],         accept: { '.tex': [], '.docx': [] }, endpoint: null,                                resultType: 'download',    group: null },
  { id: 'ltw-article', icon: 'ArrowLeftRight', accentColor: '#06b6d4', inputFmt: 'TEX',  outputFmt: 'DOCX', bidir: true,  features: ['conference','journal','publishers','ar-fa-en','math-safe'],              accept: { '.tex': [], '.docx': [] }, endpoint: null,                                resultType: 'download',    group: null },
  { id: 'itl',         icon: 'ScanLine',       accentColor: '#f0a500', inputFmt: 'IMG',  outputFmt: 'TEX',                features: ['ai-powered','pix2tex','math-only'],                                      accept: { '.png': [], '.jpg': [], '.jpeg': [] }, endpoint: '/api/convert/image-to-latex', resultType: 'latex_code', group: null },
  { id: 'ptl-text',    icon: 'AlignLeft',      accentColor: '#3b82f6', inputFmt: 'PDF',  outputFmt: 'TEX',                features: ['text-only','ar-fa-en'],                                                  accept: { '.pdf': [] },             endpoint: '/api/extract/pdf-text-latex',       resultType: 'download',    group: 'PDF → LaTeX' },
  { id: 'ptl-math',    icon: 'Sigma',          accentColor: '#8b5cf6', inputFmt: 'PDF',  outputFmt: 'TEX',                features: ['equations','ar-fa-en'],                                                  accept: { '.pdf': [] },             endpoint: '/api/extract/pdf-math-latex',       resultType: 'download',    group: 'PDF → LaTeX' },
  { id: 'ptl-figures', icon: 'Image',          accentColor: '#10b981', inputFmt: 'PDF',  outputFmt: 'ZIP',                features: ['figures','ar-fa-en'],                                                    accept: { '.pdf': [] },             endpoint: '/api/extract/pdf-figures-latex',    resultType: 'download',    group: 'PDF → LaTeX' },
  { id: 'ptl-tables',  icon: 'Table',          accentColor: '#f59e0b', inputFmt: 'PDF',  outputFmt: 'TEX',                features: ['tables','booktabs','ar-fa-en'],                                          accept: { '.pdf': [] },             endpoint: '/api/extract/pdf-tables-latex',     resultType: 'download',    group: 'PDF → LaTeX' },
  { id: 'ltrans-en',   icon: 'Languages',      accentColor: '#22c55e', inputFmt: 'TEX',  outputFmt: 'TEX',                features: ['→ English','math-safe','ar-fa-src'],                                     accept: { '.tex': [] },             endpoint: '/api/convert/tex-translate-en',     resultType: 'download',    group: 'LaTeX Translation' },
  { id: 'ltrans-ar',   icon: 'Languages',      accentColor: '#f97316', inputFmt: 'TEX',  outputFmt: 'TEX',                features: ['→ Arabic','math-safe','en-fa-src'],                                      accept: { '.tex': [] },             endpoint: '/api/convert/tex-translate-ar',     resultType: 'download',    group: 'LaTeX Translation' },
  { id: 'ltrans-fa',   icon: 'Languages',      accentColor: '#8b5cf6', inputFmt: 'TEX',  outputFmt: 'TEX',                features: ['→ Farsi','math-safe','en-ar-src'],                                       accept: { '.tex': [] },             endpoint: '/api/convert/tex-translate-fa',     resultType: 'download',    group: 'LaTeX Translation' },
  { id: 'merge-word',  icon: 'FilePlus',       accentColor: '#6366f1', inputFmt: 'DOCX', outputFmt: 'DOCX',               features: ['multi-file','merge','order'],                                            accept: { '.docx': [] },            endpoint: '/api/convert/merge-word',           resultType: 'download',    group: 'Utilities' },
  { id: 'merge-pdf',   icon: 'FilePlus2',      accentColor: '#ef4444', inputFmt: 'PDF',  outputFmt: 'PDF',                features: ['multi-file','merge','order','pdf-merge'],                                accept: { '.pdf': [] },             endpoint: '/api/convert/merge-pdf',            resultType: 'download',    group: 'Utilities' },
  { id: 'merge-images',icon: 'Images',         accentColor: '#8b5cf6', inputFmt: 'IMG',  outputFmt: 'PDF',                features: ['multi-file','merge','order','all-formats'],                              accept: { 'image/*': [] },          endpoint: '/api/convert/merge-images-to-pdf',  resultType: 'download',    group: 'Utilities' },
  { id: 'pptx-pdf',   icon: 'Presentation',   accentColor: '#f59e0b', inputFmt: 'PPTX', outputFmt: 'PDF',               features: ['multi-file','slides','pptx-conv','ar-fa-en'],                            accept: { '.ppt': [], '.pptx': [] }, endpoint: '/api/convert/pptx-to-pdf',         resultType: 'download',    group: 'Utilities' },
  { id: 'split-pdf',  icon: 'Scissors',       accentColor: '#ef4444', inputFmt: 'PDF',  outputFmt: 'ZIP',               features: ['split','ranges','chunks','individual'],                                  accept: { '.pdf': [] },             endpoint: '/api/convert/split-pdf',           resultType: 'download',    group: 'Split Tools' },
  { id: 'split-word', icon: 'Scissors',       accentColor: '#2563eb', inputFmt: 'DOCX', outputFmt: 'ZIP',               features: ['split','heading1','page-break'],                                         accept: { '.docx': [] },            endpoint: '/api/convert/split-word',          resultType: 'download',    group: 'Split Tools' },
  { id: 'split-pptx', icon: 'Scissors',       accentColor: '#f97316', inputFmt: 'PPTX', outputFmt: 'ZIP',               features: ['split','ranges','chunks','individual'],                                  accept: { '.pptx': [], '.ppt': [] }, endpoint: '/api/convert/split-pptx',         resultType: 'download',    group: 'Split Tools' },
  { id: 'compress',   icon: 'Archive',        accentColor: '#10b981', inputFmt: 'ANY',  outputFmt: 'Compressed',        features: ['pdf','images','docx','pptx','any-file','size-reduction'],                accept: {},                         endpoint: '/api/convert/compress',            resultType: 'download',    group: 'Compress' },
  { id: 'pdf-rtl-word', icon: 'FileDown',   accentColor: '#00d4aa', inputFmt: 'PDF',  outputFmt: 'DOCX+TEX',         features: ['rtl','font-based','ar-fa-en','math-safe','equations'],                    accept: { '.pdf': [] },             endpoint: null,                               resultType: 'download',    group: 'PDF → Word' },
  { id: 'whiteboard',  icon: 'Pen',        accentColor: '#00CD9C', inputFmt: 'CANVAS', outputFmt: 'PNG',              features: ['drawing','shapes','auto-correct','templates','sticky-notes','save-png'],  accept: {},                         endpoint: null,                               resultType: 'canvas',      group: null },
];

const MODES_MAP = Object.fromEntries(MODES.map(m => [m.id, m]));

const SIDEBAR_TREE = {
  documents: [
    {
      id: 'pdf-convert', accent: '#00d4aa',
      label: { en: 'PDF → LaTeX Conversion', ar: 'تحويل PDF إلى LaTeX', fa: 'تبدیل PDF به LaTeX' },
      children: [
        { modeId: 'ptl-text',    label: { en: 'Extract Text',          ar: 'استخراج النصوص',             fa: 'استخراج متن' } },
        { modeId: 'ptl-math',    label: { en: 'Extract Equations',     ar: 'استخراج المعادلات',          fa: 'استخراج معادلات' } },
        { modeId: 'ptl-figures', label: { en: 'Extract Figures',       ar: 'استخراج الأشكال',            fa: 'استخراج تصاویر' } },
        { modeId: 'ptl-tables',  label: { en: 'Extract Tables',        ar: 'استخراج الجداول',            fa: 'استخراج جداول' } },
      ],
    },
    {
      id: 'word-to-latex', accent: '#4e9af1',
      label: { en: 'Word → LaTeX', ar: 'Word إلى LaTeX', fa: 'Word به LaTeX' },
      children: [
        { modeId: 'ltw',         convDir: 'w2l', label: { en: 'Thesis / Document',  ar: 'رسالة / مستند',   fa: 'پایان‌نامه / سند' } },
        { modeId: 'ltw-article', convDir: 'w2l', label: { en: 'Academic Article',   ar: 'مقالة أكاديمية', fa: 'مقاله علمی' } },
      ],
    },
    {
      id: 'latex-to-word', accent: '#e94560',
      label: { en: 'LaTeX → Word', ar: 'LaTeX إلى Word', fa: 'LaTeX به Word' },
      children: [
        { modeId: 'ltw',         convDir: 'l2w', label: { en: 'Thesis / Document',  ar: 'رسالة / مستند',   fa: 'پایان‌نامه / سند' } },
        { modeId: 'ltw-article', convDir: 'l2w', label: { en: 'Academic Article',   ar: 'مقالة أكاديمية', fa: 'مقاله علمی' } },
      ],
    },
    {
      id: 'latex-translation', accent: '#22c55e',
      label: { en: 'LaTeX Translation', ar: 'ترجمة LaTeX', fa: 'ترجمه LaTeX' },
      children: [
        { modeId: 'ltrans-en', label: { en: '→ English', ar: '← إنجليزي', fa: '← انگلیسی' } },
        { modeId: 'ltrans-ar', label: { en: '→ Arabic',  ar: '← عربي',    fa: '← عربی' } },
        { modeId: 'ltrans-fa', label: { en: '→ Farsi',   ar: '← فارسي',   fa: '← فارسی' } },
      ],
    },
    {
      id: 'utilities', accent: '#6366f1',
      label: { en: 'Merge & Split', ar: 'الدمج والتقسيم', fa: 'ادغام و تقسیم' },
      children: [
        { modeId: 'merge-word',   label: { en: 'Merge Word Files',    ar: 'دمج ملفات Word',         fa: 'ادغام فایل‌های Word' } },
        { modeId: 'merge-pdf',   label: { en: 'Merge PDF Files',     ar: 'دمج ملفات PDF',          fa: 'ادغام فایل‌های PDF' } },
        { modeId: 'merge-images',label: { en: 'Images → PDF',        ar: 'صور إلى PDF',            fa: 'تصاویر به PDF' } },
        { modeId: 'pptx-pdf',   label: { en: 'PowerPoint → PDF',    ar: 'PowerPoint ← PDF',       fa: 'PowerPoint به PDF' } },
        { modeId: 'split-pdf',  label: { en: 'Split PDF',            ar: 'تقسيم PDF',              fa: 'تقسیم PDF' } },
        { modeId: 'split-word', label: { en: 'Split Word',           ar: 'تقسيم Word',             fa: 'تقسیم Word' } },
        { modeId: 'split-pptx', label: { en: 'Split PowerPoint',     ar: 'تقسيم PowerPoint',       fa: 'تقسیم PowerPoint' } },
      ],
    },
    {
      id: 'pdf-to-word', accent: '#00d4aa',
      label: { en: 'PDF → Word (RTL/LTR)', ar: 'PDF إلى Word (RTL/LTR)', fa: 'PDF به Word (RTL/LTR)' },
      children: [
        { modeId: 'pdf-rtl-word', label: { en: 'PDF → Word + LaTeX', ar: 'PDF إلى Word + LaTeX', fa: 'PDF به Word + LaTeX' } },
      ],
    },
    {
      id: 'compress', accent: '#10b981',
      label: { en: 'Compress Files', ar: 'ضغط الملفات', fa: 'فشرده‌سازی فایل‌ها' },
      children: [
        { modeId: 'compress', label: { en: 'Compress (PDF · Images · DOCX · PPTX · Any)', ar: 'ضغط الملفات (PDF · صور · DOCX · PPTX)', fa: 'فشرده‌سازی (PDF · تصویر · DOCX · PPTX)' } },
      ],
    },
    {
      id: 'drawing-tools', accent: '#00CD9C',
      label: { en: 'Drawing Tools', ar: 'أدوات الرسم', fa: 'ابزارهای ترسیم' },
      children: [
        { modeId: 'whiteboard', label: { en: 'Smart Whiteboard', ar: 'اللوحة البيضاء الذكية', fa: 'تخته سفید هوشمند' } },
      ],
    },
  ],
  images: [
    {
      id: 'image-to-latex', accent: '#f0a500',
      label: { en: 'Image → LaTeX', ar: 'صورة إلى LaTeX', fa: 'تصویر به LaTeX' },
      children: [
        { modeId: 'itl', label: { en: 'Math Equation (pix2tex)', ar: 'معادلة رياضية (pix2tex)', fa: 'معادله ریاضی (pix2tex)' } },
      ],
    },
  ],
};

const CHIP_COLORS = {
  'drawing':'#00CD9C','shapes':'#1CB0F6','auto-correct':'#58CC02','templates':'#CE82FF','sticky-notes':'#F9A825','save-png':'#1CB0F6',
  'math-safe':'#8b5cf6','rtl':'#00d4aa','xelatex':'#4e9af1','auto-preamble':'#4e9af1',
  'structure':'#00d4aa','math-blocks':'#8b5cf6','headings':'#f97316','ai-powered':'#f0a500',
  'pix2tex':'#f0a500','math-only':'#e94560','rtl-aware':'#00d4aa','structure-aware':'#f97316',
  'text-only':'#3b82f6','equations':'#8b5cf6','figures':'#10b981','tables':'#f59e0b',
  'booktabs':'#f59e0b','ar-fa-en':'#00d4aa','pgfplots':'#f59e0b','en-only':'#6b7280','local':'#22c55e','tikz':'#a855f7','charts':'#ec4899','diagrams':'#8b5cf6',
  'smart-ocr':'#00d4aa','fine-tuned':'#f0a500','tesseract':'#4e9af1',
  'multi-file':'#6366f1','merge':'#6366f1','order':'#a78bfa','pdf-merge':'#ef4444','slides':'#f59e0b','pptx-conv':'#f59e0b',
  'split':'#ef4444','ranges':'#f97316','chunks':'#f59e0b','individual':'#22c55e','heading1':'#2563eb','page-break':'#6366f1',
  '→ English':'#22c55e',
  '→ Arabic':'#f97316','→ Farsi':'#8b5cf6','ar-fa-src':'#00d4aa','en-fa-src':'#00d4aa',
  'en-ar-src':'#00d4aa','bidirectional':'#e94560','per-run':'#00d4aa','thesis':'#a855f7',
  'conference':'#06b6d4','journal':'#3b82f6','publishers':'#f0a500','font-based':'#8b5cf6',
};

const CHIP_LABELS = {
  'drawing':       { en:'Drawing',          ar:'الرسم',                fa:'رسم' },
  'shapes':        { en:'Shapes',           ar:'الأشكال',              fa:'اشکال' },
  'auto-correct':  { en:'Auto-correct',     ar:'تصحيح تلقائي',         fa:'اصلاح خودکار' },
  'templates':     { en:'Templates',        ar:'القوالب',              fa:'الگوها' },
  'sticky-notes':  { en:'Sticky Notes',     ar:'ملاحظات لاصقة',        fa:'یادداشت‌های چسبنده' },
  'save-png':      { en:'Save PNG',         ar:'حفظ PNG',              fa:'ذخیره PNG' },
  'math-safe':     { en:'Math Safe',       ar:'معادلات محمية',        fa:'معادلات محافظت‌شده' },
  'rtl':           { en:'RTL',             ar:'دعم RTL',              fa:'RTL' },
  'structure':     { en:'Structure',       ar:'هيكل',                 fa:'ساختار' },
  'math-blocks':   { en:'Math Blocks',     ar:'كتل رياضية',           fa:'بلوک معادلات' },
  'headings':      { en:'Headings',        ar:'عناوين',               fa:'عناوین' },
  'ai-powered':    { en:'AI Powered',      ar:'ذكاء اصطناعي',         fa:'هوش مصنوعی' },
  'pix2tex':       { en:'pix2tex',         ar:'pix2tex',              fa:'pix2tex' },
  'math-only':     { en:'Math Only',       ar:'معادلات فقط',          fa:'فقط معادلات' },
  'structure-aware':{ en:'Layout Aware',   ar:'حفظ التخطيط',          fa:'حفظ چیدمان' },
  'text-only':     { en:'Text Only',       ar:'نص فقط',               fa:'فقط متن' },
  'equations':     { en:'Equations',       ar:'معادلات',              fa:'معادلات' },
  'figures':       { en:'Figures',         ar:'الأشكال',              fa:'تصاویر' },
  'tables':        { en:'Tables',          ar:'جداول',                fa:'جداول' },
  'booktabs':      { en:'booktabs',        ar:'booktabs',             fa:'booktabs' },
  'ar-fa-en':      { en:'AR · FA · EN',    ar:'عربي · فارسي · إنجليزي', fa:'عربی · فارسی · انگلیسی' },
  'en-only':       { en:'English Only',    ar:'إنجليزي فقط',          fa:'فقط انگلیسی' },
  'multi-file':    { en:'Multi-File',      ar:'ملفات متعددة',         fa:'چند فایل' },
  'merge':         { en:'Merge',           ar:'دمج',                  fa:'ادغام' },
  'order':         { en:'Custom Order',    ar:'ترتيب مخصص',           fa:'ترتیب دلخواه' },
  'pdf-merge':     { en:'PDF Merge',       ar:'دمج PDF',              fa:'ادغام PDF' },
  'slides':        { en:'Slides',         ar:'شرائح',                fa:'اسلایدها' },
  'pptx-conv':     { en:'PPTX→PDF',       ar:'PPTX←PDF',             fa:'PPTX به PDF' },
  '→ English':     { en:'→ English',       ar:'← إنجليزي',            fa:'← انگلیسی' },
  '→ Arabic':      { en:'→ Arabic',        ar:'← عربي',               fa:'← عربی' },
  '→ Farsi':       { en:'→ Farsi',         ar:'← فارسي',              fa:'← فارسی' },
  'ar-fa-src':     { en:'AR · FA src',     ar:'المصدر: عربي · فارسي', fa:'منبع: عربی · فارسی' },
  'en-fa-src':     { en:'EN · FA src',     ar:'المصدر: إنجليزي · فارسي', fa:'منبع: انگلیسی · فارسی' },
  'en-ar-src':     { en:'EN · AR src',     ar:'المصدر: إنجليزي · عربي',  fa:'منبع: انگلیسی · عربی' },
  'bidirectional': { en:'Bidirectional',   ar:'ثنائي الاتجاه',        fa:'دوطرفه' },
  'per-run':       { en:'Per-Run RTL',     ar:'RTL لكل نص',           fa:'RTL هر رشته' },
  'thesis':        { en:'Thesis',          ar:'رسائل وأطروحات',       fa:'پایان‌نامه' },
  'conference':    { en:'Conference',      ar:'مؤتمرات',              fa:'کنفرانس' },
  'journal':       { en:'Journal',         ar:'مجلات علمية',          fa:'مجله علمی' },
  'publishers':    { en:'IEEE·ACM·Springer·Elsevier', ar:'IEEE·ACM·Springer·Elsevier', fa:'IEEE·ACM·Springer·Elsevier' },
  'smart-ocr':     { en:'Smart OCR',       ar:'تعرف ذكي',             fa:'OCR هوشمند' },
  'fine-tuned':    { en:'Fine-Tuned',      ar:'مُحسَّن',               fa:'فاین‌تیون' },
  'tesseract':     { en:'Tesseract OCR',   ar:'Tesseract OCR',        fa:'Tesseract OCR' },
  'font-based':    { en:'Font-Based',      ar:'قائم على الخط',        fa:'مبتنی بر فونت' },
  'split':         { en:'Split',           ar:'تقسيم',                fa:'تقسیم' },
  'ranges':        { en:'Ranges',          ar:'نطاقات',               fa:'محدوده‌ها' },
  'chunks':        { en:'Equal Chunks',    ar:'أجزاء متساوية',        fa:'قطعات مساوی' },
  'individual':    { en:'Individual',      ar:'منفرد',                fa:'جداگانه' },
  'heading1':      { en:'By Heading 1',    ar:'بالعنوان 1',           fa:'بر اساس عنوان ۱' },
  'page-break':    { en:'Page Break',      ar:'فاصل صفحة',            fa:'شکست صفحه' },
  'pgfplots':      { en:'PGFPlots',        ar:'PGFPlots',              fa:'PGFPlots' },
  'local':         { en:'100% Local',     ar:'محلي 100٪',             fa:'۱۰۰٪ محلی' },
  'tikz':          { en:'TikZ',           ar:'TikZ',                  fa:'TikZ' },
  'charts':        { en:'Charts',         ar:'مخططات',                fa:'نمودارها' },
  'diagrams':      { en:'Diagrams',       ar:'أشكال بيانية',           fa:'دیاگرام‌ها' },
};

const MODE_BULLETS = {
  ltw: {
    en: ['Full thesis / dissertation support', 'Bidirectional: LaTeX ↔ Word', 'Arabic, Farsi, and English RTL/LTR', 'Math-safe: equations preserved exactly', 'Per-run RTL text detection', 'Auto TOC generation'],
    ar: ['دعم كامل للرسائل والأطروحات', 'تحويل ثنائي الاتجاه: LaTeX ↔ Word', 'دعم العربية والفارسية والإنجليزية', 'حماية المعادلات الرياضية', 'كشف RTL لكل مقطع نصي', 'إنشاء تلقائي لفهرس المحتويات'],
    fa: ['پشتیبانی کامل از پایان‌نامه', 'تبدیل دوطرفه: LaTeX ↔ Word', 'پشتیبانی از عربی، فارسی و انگلیسی', 'حفظ معادلات ریاضی', 'تشخیص RTL هر رشته', 'تولید خودکار فهرست مطالب'],
  },
  'ltw-article': {
    en: ['Academic journal & conference articles', 'IEEE, ACM, Springer, Elsevier templates', 'Two-column layout preserved', 'Math-safe: equations unchanged', 'Arabic / Farsi / English content'],
    ar: ['مقالات المجلات العلمية والمؤتمرات', 'قوالب IEEE و ACM و Springer و Elsevier', 'الحفاظ على تخطيط العمودين', 'حماية المعادلات الرياضية', 'دعم المحتوى العربي والفارسي'],
    fa: ['مقالات علمی و کنفرانسی', 'قالب‌های IEEE، ACM، Springer، Elsevier', 'حفظ چیدمان دو ستونی', 'حفظ معادلات ریاضی', 'پشتیبانی از محتوای عربی/فارسی'],
  },
  itl: {
    en: ['Math equation image to LaTeX', 'Powered by pix2tex model', 'Handles complex equations', 'Output is editable LaTeX code'],
    ar: ['تحويل صور المعادلات إلى LaTeX', 'مدعوم بنموذج pix2tex', 'يتعامل مع المعادلات المعقدة', 'الناتج كود LaTeX قابل للتعديل'],
    fa: ['تبدیل تصویر معادله به LaTeX', 'مبتنی بر مدل pix2tex', 'پشتیبانی از معادلات پیچیده', 'خروجی کد LaTeX قابل ویرایش'],
  },
  'ptl-text': {
    en: ['Extract plain text from PDF', 'Arabic, Farsi, English support', 'Outputs clean LaTeX text body', 'Preserves paragraph structure'],
    ar: ['استخراج النص من PDF', 'دعم العربية والفارسية والإنجليزية', 'إخراج نص LaTeX نظيف', 'الحفاظ على بنية الفقرات'],
    fa: ['استخراج متن ساده از PDF', 'پشتیبانی از عربی، فارسی، انگلیسی', 'خروجی متن LaTeX تمیز', 'حفظ ساختار پاراگراف'],
  },
  'ptl-math': {
    en: ['Extract all math equations from PDF', 'Font-based detection method', 'Arabic, Farsi, English PDFs', 'Outputs LaTeX math blocks'],
    ar: ['استخراج المعادلات الرياضية من PDF', 'طريقة الكشف القائمة على الخط', 'دعم ملفات PDF متعددة اللغات', 'إخراج كتل رياضية بصيغة LaTeX'],
    fa: ['استخراج معادلات ریاضی از PDF', 'روش تشخیص مبتنی بر فونت', 'پشتیبانی از PDF‌های چندزبانه', 'خروجی بلوک‌های ریاضی LaTeX'],
  },
  'ptl-figures': {
    en: ['Extract all figures from PDF', 'Output as ZIP archive', 'Arabic, Farsi, English PDFs', 'LaTeX figure environments included'],
    ar: ['استخراج جميع الأشكال من PDF', 'الإخراج كأرشيف ZIP', 'دعم ملفات PDF متعددة اللغات', 'تضمين بيئات الأشكال في LaTeX'],
    fa: ['استخراج همه تصاویر از PDF', 'خروجی به صورت آرشیو ZIP', 'پشتیبانی از PDF‌های چندزبانه', 'محیط‌های figure در LaTeX'],
  },
  'ptl-tables': {
    en: ['Extract tables from PDF to LaTeX', 'booktabs-quality formatting', 'Arabic, Farsi, English PDFs', 'Complex multi-column tables'],
    ar: ['استخراج الجداول من PDF إلى LaTeX', 'تنسيق عالي الجودة (booktabs)', 'دعم ملفات PDF متعددة اللغات', 'جداول متعددة الأعمدة'],
    fa: ['استخراج جداول از PDF به LaTeX', 'قالب‌بندی با کیفیت booktabs', 'پشتیبانی از PDF‌های چندزبانه', 'جداول پیچیده چند ستونی'],
  },
  'ltrans-en': {
    en: ['Translate LaTeX source to English', 'Math environments preserved', 'Arabic and Farsi source support', 'Commands and macros untouched'],
    ar: ['ترجمة مصدر LaTeX إلى الإنجليزية', 'الحفاظ على البيئات الرياضية', 'دعم المصادر العربية والفارسية', 'الأوامر والماكرو بدون تغيير'],
    fa: ['ترجمه منبع LaTeX به انگلیسی', 'حفظ محیط‌های ریاضی', 'پشتیبانی از منابع عربی و فارسی', 'دستورات و ماکروها دست‌نخورده'],
  },
  'ltrans-ar': {
    en: ['Translate LaTeX source to Arabic', 'Math environments preserved', 'English and Farsi source support', 'RTL output with proper encoding'],
    ar: ['ترجمة مصدر LaTeX إلى العربية', 'الحفاظ على البيئات الرياضية', 'دعم المصادر الإنجليزية والفارسية', 'إخراج RTL مع ترميز صحيح'],
    fa: ['ترجمه منبع LaTeX به عربی', 'حفظ محیط‌های ریاضی', 'پشتیبانی از منابع انگلیسی و فارسی', 'خروجی RTL با رمزگذاری صحیح'],
  },
  'ltrans-fa': {
    en: ['Translate LaTeX source to Farsi', 'Math environments preserved', 'English and Arabic source support', 'RTL output with proper encoding'],
    ar: ['ترجمة مصدر LaTeX إلى الفارسية', 'الحفاظ على البيئات الرياضية', 'دعم المصادر الإنجليزية والعربية', 'إخراج RTL مع ترميز صحيح'],
    fa: ['ترجمه منبع LaTeX به فارسی', 'حفظ محیط‌های ریاضی', 'پشتیبانی از منابع انگلیسی و عربی', 'خروجی RTL با رمزگذاری صحیح'],
  },
  'merge-word': {
    en: ['Merge multiple Word files into one', 'Custom ordering of documents', 'Preserves styles and formatting', 'Output as single .docx file'],
    ar: ['دمج ملفات Word متعددة في ملف واحد', 'ترتيب مخصص للمستندات', 'الحفاظ على الأنماط والتنسيق', 'الإخراج كملف .docx واحد'],
    fa: ['ادغام چند فایل Word به یکی', 'ترتیب دلخواه اسناد', 'حفظ سبک‌ها و قالب‌بندی', 'خروجی یک فایل .docx'],
  },
  'merge-pdf': {
    en: ['Merge multiple PDF files into one', 'Three merge modes: seamless, separator, normalize', 'Custom page ordering', 'Output as single .pdf file'],
    ar: ['دمج ملفات PDF متعددة في ملف واحد', 'ثلاثة أنماط: سلس، فاصل، توحيد', 'ترتيب مخصص للصفحات', 'الإخراج كملف .pdf واحد'],
    fa: ['ادغام چند فایل PDF در یکی', 'سه حالت: یکپارچه، جداکننده، یکسان‌سازی', 'ترتیب دلخواه صفحات', 'خروجی یک فایل .pdf'],
  },
  'merge-images': {
    en: ['Convert multiple images into a single PDF', 'Supports PNG, JPG, GIF, BMP, TIFF, WebP, SVG, ICO & more', 'Custom ordering — drag to reorder', 'Each image becomes one page'],
    ar: ['تحويل صور متعددة إلى PDF واحد', 'يدعم PNG وJPG وGIF وBMP وTIFF وWebP وSVG وICO وغيرها', 'ترتيب مخصص — اسحب لإعادة الترتيب', 'كل صورة تصبح صفحة واحدة'],
    fa: ['تبدیل چند تصویر به یک PDF', 'پشتیبانی از PNG، JPG، GIF، BMP، TIFF، WebP، SVG، ICO و بیشتر', 'ترتیب دلخواه — برای مرتب‌سازی بکشید', 'هر تصویر یک صفحه می‌شود'],
  },
  'pptx-pdf': {
    en: ['Convert .ppt/.pptx to PDF', 'Direct: preserves slides layout exactly', 'Reflow: extracts content as readable document', 'Slide titles become document headings'],
    ar: ['تحويل .ppt/.pptx إلى PDF', 'مباشر: يحافظ على تخطيط الشرائح تماماً', 'إعادة التدفق: يستخرج المحتوى كمستند قابل للقراءة', 'عناوين الشرائح تصبح عناوين المستند'],
    fa: ['تبدیل .ppt/.pptx به PDF', 'مستقیم: چیدمان اسلایدها دقیقاً حفظ می‌شود', 'بازآرایی: محتوا به سند خوانا تبدیل می‌شود', 'عنوان اسلایدها به heading تبدیل می‌شوند'],
  },
  'split-pdf': {
    en: ['Split a PDF into multiple files', 'Individual: one PDF per page', 'Custom ranges: e.g. 1-3, 5, 7-10', 'Equal chunks: N pages per part', 'Output is a ZIP of PDF files'],
    ar: ['تقسيم ملف PDF إلى ملفات متعددة', 'منفرد: ملف PDF لكل صفحة', 'نطاقات مخصصة: مثال 1-3، 5، 7-10', 'أجزاء متساوية: N صفحة لكل جزء', 'الناتج ملف ZIP يحتوي ملفات PDF'],
    fa: ['تقسیم PDF به چند فایل', 'جداگانه: یک PDF برای هر صفحه', 'محدوده دلخواه: مثال 1-3، 5، 7-10', 'قطعات مساوی: N صفحه در هر بخش', 'خروجی فایل ZIP حاوی PDFها'],
  },
  'split-word': {
    en: ['Split a .docx into multiple files', 'By page break: split at Word page-break markers', 'By Heading 1: each section starts a new document', 'Styles and formatting preserved in every part', 'Output is a ZIP of DOCX files'],
    ar: ['تقسيم ملف .docx إلى ملفات متعددة', 'عند فاصل الصفحة: التقسيم عند علامات كسر الصفحة', 'بالعنوان 1: كل قسم يبدأ مستنداً جديداً', 'يحافظ على الأنماط والتنسيق في كل جزء', 'الناتج ملف ZIP يحتوي ملفات DOCX'],
    fa: ['تقسیم .docx به چند فایل', 'بر اساس شکست صفحه: تقسیم در نقاط شکست صفحه Word', 'بر اساس عنوان ۱: هر بخش سند جدیدی می‌شود', 'استایل‌ها و قالب‌بندی در هر بخش حفظ می‌شود', 'خروجی فایل ZIP حاوی فایل‌های DOCX'],
  },
  'split-pptx': {
    en: ['Split a .pptx into multiple files', 'Individual: one PPTX per slide', 'Custom ranges: e.g. 1-3, 5, 7-10', 'Equal chunks: N slides per part', 'Output is a ZIP of PPTX files'],
    ar: ['تقسيم ملف .pptx إلى ملفات متعددة', 'منفرد: ملف PPTX لكل شريحة', 'نطاقات مخصصة: مثال 1-3، 5، 7-10', 'أجزاء متساوية: N شريحة لكل جزء', 'الناتج ملف ZIP يحتوي ملفات PPTX'],
    fa: ['تقسیم .pptx به چند فایل', 'جداگانه: یک PPTX برای هر اسلاید', 'محدوده دلخواه: مثال 1-3، 5، 7-10', 'قطعات مساوی: N اسلاید در هر بخش', 'خروجی فایل ZIP حاوی فایل‌های PPTX'],
  },
  humanize: {
    en: ['Rewrites AI-generated academic text to sound natural', 'Math equations, citations, and numbers preserved exactly', 'Meaning-preservation verification after every rewrite', 'AI pattern score shown before and after'],
    ar: ['يُعيد صياغة النصوص الأكاديمية لتبدو طبيعية', 'حماية كاملة للمعادلات والاستشهادات والأرقام', 'التحقق من الحفاظ على المعنى بعد كل إعادة كتابة', 'عرض نمط الذكاء الاصطناعي قبل وبعد المعالجة'],
    fa: ['بازنویسی متن آکادمیک تولیدشده توسط هوش مصنوعی', 'معادلات، ارجاعات و اعداد کاملاً حفظ می‌شوند', 'تأیید حفظ معنا پس از هر بازنویسی', 'امتیاز الگوی هوش مصنوعی قبل و بعد نمایش داده می‌شود'],
  },
  'pdf-rtl-word': {
    en: ['Converts academic PDFs to Word and LaTeX', 'Handles Arabic, Farsi, and English text', 'Font-based math detection — equations as images', 'Per-run RTL/LTR direction in output docx', 'Download Word (.docx) or LaTeX + assets (.zip)'],
    ar: ['يحول ملفات PDF الأكاديمية إلى Word وLaTeX', 'يدعم النصوص العربية والفارسية والإنجليزية', 'كشف المعادلات بالخط — المعادلات كصور', 'اتجاه RTL/LTR لكل مقطع نصي في ملف Word', 'تنزيل Word (.docx) أو LaTeX + الصور (.zip)'],
    fa: ['تبدیل PDF‌های علمی به Word و LaTeX', 'پشتیبانی از متن عربی، فارسی و انگلیسی', 'تشخیص معادلات بر اساس فونت — معادلات به صورت تصویر', 'جهت RTL/LTR برای هر بخش متنی در خروجی Word', 'دانلود Word (.docx) یا LaTeX + تصاویر (.zip)'],
  },
  whiteboard: {
    en: ['Draw with pen, marker, or highlighter', 'Auto-correct freehand shapes to perfect geometry', 'Teaching templates: tense tables, timelines, diagrams', 'Vocabulary lookup with canvas stamp', 'Sticky notes overlay — drag to move', 'Save board as PNG or to cloud'],
    ar: ['الرسم بالقلم أو الماركر أو القلم الفسفوري', 'تصحيح تلقائي للأشكال المرسومة يدوياً إلى هندسة مثالية', 'قوالب تعليمية: جداول الأزمنة، خطوط الزمن، المخططات', 'البحث عن المفردات مع الطوابع على اللوحة', 'ملاحظات لاصقة قابلة للسحب', 'حفظ اللوحة كصورة PNG أو في السحابة'],
    fa: ['رسم با قلم، ماژیک، یا هایلایتر', 'اصلاح خودکار اشکال دست‌آزاد به هندسه کامل', 'الگوهای آموزشی: جداول زمان، خط زمانی، نمودارها', 'جستجوی واژگان با مهر روی بوم', 'یادداشت‌های چسبنده قابل کشیدن', 'ذخیره بوم به صورت PNG یا در ابر'],
  },
};

function useParticles(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const N = 65;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.4 + 0.5,
    }));

    const shapes = Array.from({ length: 11 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.15,
      vy: (Math.random() - 0.5) * 0.15,
      size: Math.random() * 26 + 12,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.003,
      sides: Math.random() > 0.5 ? 3 : 6,
      color: Math.random() > 0.5 ? '#4e9af1' : '#00d4aa',
      opacity: Math.random() * 0.055 + 0.025,
    }));

    const drawPoly = (x, y, size, rot, sides) => {
      ctx.beginPath();
      for (let i = 0; i < sides; i++) {
        const a = (i / sides) * Math.PI * 2 + rot - Math.PI / 2;
        const px = x + Math.cos(a) * size;
        const py = y + Math.sin(a) * size;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      shapes.forEach(s => {
        ctx.globalAlpha = s.opacity;
        ctx.strokeStyle = s.color;
        ctx.lineWidth = 1;
        drawPoly(s.x, s.y, s.size, s.rot, s.sides);
        ctx.stroke();
        s.x += s.vx; s.y += s.vy; s.rot += s.rotSpeed;
        if (s.x < -60 || s.x > canvas.width + 60) s.vx *= -1;
        if (s.y < -60 || s.y > canvas.height + 60) s.vy *= -1;
      });

      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            ctx.globalAlpha = (1 - d / 140) * 0.22;
            ctx.strokeStyle = '#4e9af1';
            ctx.lineWidth = 0.7;
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      pts.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = Math.random() > 0.5 ? 'rgba(78,154,241,0.55)' : 'rgba(0,212,170,0.45)';
        ctx.fill();
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);
}

const MODE_EMOJI = {
  ltw: '⇄', 'ltw-article': '📄', itl: '∑',
  'ptl-text': '📃', 'ptl-math': 'Σ', 'ptl-figures': '🖼', 'ptl-tables': '⊞',
  'ltrans-en': '🇬🇧', 'ltrans-ar': '🇸🇦', 'ltrans-fa': '🇮🇷', 'merge-word': '🗂', 'merge-pdf': '📑', 'merge-images': '🖼️', 'pptx-pdf': '📊',
  'split-pdf': '✂', 'split-word': '✂', 'split-pptx': '✂',
  humanize: '✍',
  'pdf-rtl-word': '📑',
  whiteboard: '⬜',
};

export default function AppPage() {
  const { t, lang, dir } = useLang();
  const { isAuthenticated, token } = useAuth();
  const isRTL = lang !== 'en';

  const [selectedMode, setSelectedMode] = useState(null);
  const [convDir, setConvDir] = useState(null);
  const [articlePublisher, setArticlePublisher] = useState('arxiv');
  const [articlePubType, setArticlePubType] = useState('journal');
  const [articleRefStyle, setArticleRefStyle] = useState('sn-mathphys-num');
  const [articleAcmType, setArticleAcmType] = useState('sigconf');
  const [articleElsevierModel, setArticleElsevierModel] = useState('num');
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [editorData, setEditorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serverOnline, setServerOnline] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('documents');
  const [expandedCats, setExpandedCats] = useState(new Set(['pdf-convert']));
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const canvasRef = useRef(null);
  useParticles(canvasRef);

  useEffect(() => {
    const check = async () => {
      try { await axios.get(`${API}/api/health`, { timeout: 5000 }); setServerOnline(true); }
      catch { setServerOnline(false); }
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    setConvDir(null);
    setArticlePublisher('arxiv');
    setArticlePubType('journal');
    setSelectedFile(null);
    setResult(null);
    setEditorData(null);
  };

  const handleSidebarLeafClick = (leaf) => {
    const mode = MODES_MAP[leaf.modeId];
    if (!mode) return;
    handleModeSelect(mode);
    if (leaf.convDir) setConvDir(leaf.convDir);
    setSidebarOpen(false);
  };

  const toggleCat = (id) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isLeafSelected = (leaf) => {
    if (!selectedMode || selectedMode.id !== leaf.modeId) return false;
    if (leaf.convDir && convDir !== leaf.convDir) return false;
    return true;
  };

  const getRuntimeMode = () => {
    if (selectedMode?.id === 'ltw') {
      if (convDir === 'l2w') return { ...selectedMode, endpoint: '/api/convert/latex-to-word',        outputFmt: 'DOCX', accept: { '.tex': [] } };
      if (convDir === 'w2l') return { ...selectedMode, endpoint: '/api/convert/word-to-latex',        outputFmt: 'TEX',  accept: { '.docx': [] } };
      return null;
    }
    if (selectedMode?.id === 'ltw-article') {
      if (convDir === 'l2w') return { ...selectedMode, endpoint: '/api/convert/article-latex-to-word', outputFmt: 'DOCX', accept: { '.tex': [], '.zip': [] } };
      if (convDir === 'w2l') return { ...selectedMode, endpoint: '/api/convert/article-word-to-latex', outputFmt: 'TEX',  accept: { '.docx': [] } };
      return null;
    }
    return selectedMode;
  };

  const getFilename = (mode, originalName) => {
    const stem = originalName.replace(/\.[^.]+$/, '');
    const extMap = {
      ltw: convDir === 'w2l' ? '.tex' : '.docx',
      'ltw-article': convDir === 'w2l' ? '.tex' : '.docx',
      'ptl-text': '.tex', 'ptl-math': '.tex', 'ptl-figures': '.zip', 'ptl-tables': '.tex',
      'ltrans-en': '.tex', 'ltrans-ar': '.tex', 'ltrans-fa': '.tex',
    };
    return stem + (extMap[mode.id] || '.bin');
  };

  const handleConvert = async () => {
    if (!selectedFile) { toast.error(t.errors.noFile); return; }
    const runtimeMode = getRuntimeMode();
    if (!runtimeMode) { toast.error(t.errors.noFile); return; }
    setLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    if (selectedMode?.id === 'ltw-article') {
      formData.append('publisher', articlePublisher);
      formData.append('pub_type', articlePubType);
      formData.append('ref_style', articleRefStyle);
      formData.append('acm_type', articleAcmType);
      formData.append('elsevier_model', articleElsevierModel);
    }
    const isDownload = runtimeMode.resultType === 'download';
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const isPdfHeavy = runtimeMode.id.startsWith('pt');
      const timeoutMs = isPdfHeavy ? 1200000 : 300000;
      const response = await axios.post(
        `${API}${runtimeMode.endpoint}`, formData,
        { responseType: isDownload ? 'blob' : 'json', headers, timeout: timeoutMs }
      );

      const contentType = (response.headers['content-type'] || '').toLowerCase();
      if (isDownload && runtimeMode.outputFmt === 'TEX' && !contentType.includes('zip')) {
        const text = await response.data.text();
        const jobId = response.headers['x-job-id'] || null;
        const pageCount = parseInt(response.headers['x-page-count'] || '1', 10);
        setEditorData({
          content: text, jobId: runtimeMode.inputFmt === 'PDF' ? jobId : null,
          pageCount: runtimeMode.inputFmt === 'PDF' ? pageCount : 1,
          outputFormat: 'latex', filename: selectedFile.name,
        });
      } else if (isDownload) {
        let previewText = null;
        try { const b64 = response.headers['x-doc-text']; if (b64) previewText = atob(b64); } catch { }
        let filename = getFilename(runtimeMode, selectedFile.name);
        if (contentType.includes('zip') && !filename.endsWith('.zip')) {
          filename = selectedFile.name.replace(/\.[^.]+$/, '') + '.zip';
        }
        setResult({ type: 'download', blob: response.data, filename, previewText });
      } else if (runtimeMode.resultType === 'tikz_code') {
        setEditorData({ content: response.data.tikz_code, jobId: null, pageCount: 1, outputFormat: 'latex', filename: selectedFile.name });
      } else {
        setEditorData({ content: response.data.latex_code, jobId: null, pageCount: 1, outputFormat: 'latex', filename: selectedFile.name });
      }

      toast.success(t.app.success, { style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' } });
    } catch (err) {
      let msg = t.errors.conversionFailed;
      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try { const text = await err.response.data.text(); msg = JSON.parse(text).detail || msg; } catch { }
        } else { msg = err.response.data.detail || msg; }
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const T = t.app;
  const currentMode = selectedMode ? MODES_MAP[selectedMode.id] : null;
  const bullets = currentMode ? (MODE_BULLETS[currentMode.id]?.[lang] || MODE_BULLETS[currentMode.id]?.en || []) : [];

  const SB = {
    bg: '#0b0d1c',
    border: isRTL ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(255,255,255,0.07)',
  };

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'linear-gradient(160deg, #04051a 0%, #070920 55%, #0a0d32 100%)', position: 'relative' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />

      {editorData && (
        <EditorPanel
          jobId={editorData.jobId} pageCount={editorData.pageCount} content={editorData.content}
          outputFormat={editorData.outputFormat} filename={editorData.filename} onClose={() => setEditorData(null)}
        />
      )}

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header serverOnline={serverOnline} />

        <div style={{ flex: 1, minHeight: 0, display: 'flex', direction: dir, overflow: 'hidden', position: 'relative' }}>

          {/* Mobile backdrop */}
          {isMobile && (
            <div
              className={`sidebar-backdrop${sidebarOpen ? ' open' : ''}`}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* ── Sidebar ── */}
          <aside
            className={`app-sidebar${isRTL ? ' rtl' : ''}${sidebarOpen ? ' open' : ''}`}
            style={{
              width: 248, flexShrink: 0, display: 'flex', flexDirection: 'column',
              background: 'rgba(7, 9, 28, 0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              borderRight: isRTL ? 'none' : '1px solid rgba(255,255,255,0.08)',
              borderLeft: isRTL ? '1px solid rgba(255,255,255,0.08)' : 'none',
              overflowY: 'auto',
              top: isMobile ? 0 : undefined,
            }}
          >
            {/* Mobile close button */}
            {isMobile && (
              <div style={{ padding: '10px 14px 0', display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '20px', cursor: 'pointer', padding: '6px', minHeight: '40px', minWidth: '40px' }}>✕</button>
              </div>
            )}

            {/* Tab switcher — underline style */}
            <div style={{ padding: '18px 14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', gap: '0' }}>
                {[
                  { key: 'documents', en: 'Documents', ar: 'مستندات', fa: 'اسناد' },
                  { key: 'images',    en: 'Images',    ar: 'صور',      fa: 'تصاویر' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setSidebarTab(tab.key)} style={{
                    flex: 1, padding: '8px 4px 10px', fontSize: '11px', fontWeight: 700,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    borderBottom: sidebarTab === tab.key ? '2px solid #4e9af1' : '2px solid transparent',
                    color: sidebarTab === tab.key ? '#4e9af1' : 'rgba(255,255,255,0.68)',
                    textTransform: 'uppercase', letterSpacing: '0.8px', transition: 'all 0.18s',
                    fontFamily: 'inherit',
                  }}>
                    {tab[lang] || tab.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Tree */}
            <div style={{ flex: 1, paddingTop: '8px', paddingBottom: '12px' }}>
              {SIDEBAR_TREE[sidebarTab].map(cat => {
                const expanded = expandedCats.has(cat.id);
                const catLabel = cat.label[lang] || cat.label.en;
                return (
                  <div key={cat.id} style={{ marginBottom: '2px' }}>
                    <button onClick={() => toggleCat(cat.id)} style={{
                      width: 'calc(100% - 16px)', margin: '2px 8px', display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '9px 12px',
                      background: expanded ? `linear-gradient(135deg, ${cat.accent}1e 0%, transparent 100%)` : 'transparent',
                      border: `1.5px solid ${expanded ? cat.accent + '40' : 'rgba(255,255,255,0.06)'}`,
                      borderRadius: '10px', cursor: 'pointer',
                      textAlign: isRTL ? 'right' : 'left', direction: dir,
                      color: expanded ? cat.accent : 'rgba(255,255,255,0.88)',
                      fontSize: '12.5px', fontWeight: 700, transition: 'all 0.18s',
                      fontFamily: 'inherit',
                    }}>
                      {isRTL ? (
                        <>
                          <span style={{ flex: 1 }}>{catLabel}</span>
                          <span style={{ fontSize: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: expanded ? 'rotate(0deg)' : 'rotate(90deg)', color: expanded ? cat.accent : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>▼</span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '8px', transition: 'transform 0.2s', display: 'inline-block', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', color: expanded ? cat.accent : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>▼</span>
                          <span style={{ flex: 1 }}>{catLabel}</span>
                        </>
                      )}
                    </button>

                    {expanded && cat.children.map(leaf => {
                      const sel = isLeafSelected(leaf);
                      const leafLabel = leaf.label[lang] || leaf.label.en;
                      const arrow = isRTL ? '← ' : '→ ';
                      return (
                        <button key={`${leaf.modeId}-${leaf.convDir || ''}`} onClick={() => handleSidebarLeafClick(leaf)} style={{
                          width: 'calc(100% - 28px)', margin: '1px 14px', padding: '7px 10px', display: 'block',
                          background: sel ? `${cat.accent}18` : 'transparent',
                          border: `1px solid ${sel ? cat.accent + '40' : 'transparent'}`,
                          borderRadius: '7px',
                          cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', direction: dir,
                          color: sel ? cat.accent : 'rgba(255,255,255,0.78)',
                          fontSize: '11.5px', fontWeight: sel ? 600 : 500, transition: 'all 0.15s',
                          fontFamily: 'inherit',
                        }}
                          onMouseEnter={e => { if (!sel) { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; } }}
                          onMouseLeave={e => { if (!sel) { e.currentTarget.style.color = 'rgba(255,255,255,0.78)'; e.currentTarget.style.background = 'transparent'; } }}
                        >
                          <span style={{ color: sel ? cat.accent : 'rgba(255,255,255,0.42)', marginRight: isRTL ? 0 : '4px', marginLeft: isRTL ? '4px' : 0 }}>{arrow}</span>{leafLabel}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Mobile sidebar FAB */}
          <button
            className={`sidebar-fab ${isRTL ? 'rtl' : 'ltr'}`}
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            ☰
          </button>

          {/* ── Main content ── */}
          <main style={{ flex: 1, minWidth: 0, overflowY: selectedMode?.id === 'whiteboard' ? 'hidden' : 'auto', overflow: selectedMode?.id === 'whiteboard' ? 'hidden' : undefined, display: 'flex', flexDirection: 'column' }}>
            {/* Banners */}
            {selectedMode?.id !== 'whiteboard' && (
            <div style={{ padding: '14px 18px 0', display: 'flex', flexDirection: 'column', gap: '8px', direction: dir }}>
              {/* guest banner hidden until payments are ready */}
            </div>
            )}

            {/* Two-panel content area */}
            {selectedMode?.id === 'whiteboard' ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                <SmartWhiteboard />
              </div>
            ) : null}
            <div className="app-panels" style={{ minHeight: 0, direction: dir, display: selectedMode?.id === 'whiteboard' ? 'none' : undefined }}>

              {currentMode ? (
                <>
                  {/* ── Left: Upload panel ── */}
                  <div className="app-upload-panel" style={{
                    background: 'rgba(10, 12, 32, 0.75)',
                    backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
                    borderRadius: '18px', border: `1px solid ${currentMode.accentColor}30`,
                    padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px',
                    boxShadow: `0 0 40px ${currentMode.accentColor}12, 0 12px 40px rgba(0,0,0,0.55)`,
                    direction: dir,
                  }}>
                    {/* Mode header */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace', color: currentMode.accentColor, background: `${currentMode.accentColor}18`, padding: '2px 7px', borderRadius: '5px', border: `1px solid ${currentMode.accentColor}30` }}>
                          {convDir === 'w2l' && currentMode.id.includes('ltw') ? 'DOCX' : currentMode.inputFmt}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>→</span>
                        <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: 'monospace', color: 'rgba(255,255,255,0.82)', background: 'rgba(255,255,255,0.07)', padding: '2px 7px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.15)' }}>
                          {convDir === 'w2l' && currentMode.id.includes('ltw') ? 'TEX' : currentMode.outputFmt}
                        </span>
                      </div>
                      <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#f0f0f5', margin: 0, lineHeight: 1.3 }}>
                        {T.modes?.[currentMode.id]?.title || currentMode.id}
                      </h2>
                    </div>

                    {/* Direction picker for ltw */}
                    {selectedMode.id === 'ltw' && (
                      <div>
                        <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '9px' }}>{t.app.dirPicker?.choose || 'Choose conversion direction'}</p>
                        <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                          {[
                            { key: 'l2w', label: t.app.dirPicker?.l2w || 'LaTeX → Word', sub: '.tex → .docx', color: '#e94560' },
                            { key: 'w2l', label: t.app.dirPicker?.w2l || 'Word → LaTeX', sub: '.docx → .tex', color: '#4e9af1' },
                          ].map(({ key, label, sub, color }) => (
                            <button key={key} onClick={() => { setConvDir(key); setSelectedFile(null); setResult(null); }} style={{
                              flex: 1, minWidth: '120px', padding: '11px 14px',
                              background: convDir === key ? `${color}18` : 'rgba(255,255,255,0.03)',
                              border: `1.5px solid ${convDir === key ? color : 'rgba(255,255,255,0.09)'}`,
                              borderRadius: '10px', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', transition: 'all 0.17s', fontFamily: 'inherit',
                            }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: convDir === key ? color : '#e0e0e8', fontFamily: 'monospace', marginBottom: '3px' }}>{label}</div>
                              <div style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.45)' }}>{sub}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Direction + publisher pickers for ltw-article */}
                    {selectedMode.id === 'ltw-article' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '9px' }}>{t.app.articlePicker?.choose || 'Choose conversion direction'}</p>
                          <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                            {[
                              { key: 'l2w', label: t.app.articlePicker?.l2w || 'LaTeX → Word', sub: '.tex → .docx', color: '#06b6d4' },
                              { key: 'w2l', label: t.app.articlePicker?.w2l || 'Word → LaTeX', sub: '.docx → .tex', color: '#4e9af1' },
                            ].map(({ key, label, sub, color }) => (
                              <button key={key} onClick={() => { setConvDir(key); setSelectedFile(null); setResult(null); }} style={{
                                flex: 1, minWidth: '120px', padding: '11px 14px',
                                background: convDir === key ? `${color}18` : 'rgba(255,255,255,0.03)',
                                border: `1.5px solid ${convDir === key ? color : 'rgba(255,255,255,0.09)'}`,
                                borderRadius: '10px', cursor: 'pointer', textAlign: isRTL ? 'right' : 'left', transition: 'all 0.17s', fontFamily: 'inherit',
                              }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: convDir === key ? color : '#e0e0e8', fontFamily: 'monospace', marginBottom: '3px' }}>{label}</div>
                                <div style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.45)' }}>{sub}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {convDir && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {/* Publisher picker */}
                            <div>
                              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '8px' }}>{t.app.articlePicker?.publisher || 'Target publisher'}</p>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[
                                  { key: 'arxiv',        label: 'arXiv' },
                                  { key: 'ieee',         label: 'IEEE' },
                                  { key: 'acm',          label: 'ACM' },
                                  { key: 'elsevier',     label: 'Elsevier' },
                                  { key: 'springer',     label: 'Springer' },
                                  { key: 'springer_lncs',label: 'Springer LNCS' },
                                ].map(({ key, label }) => (
                                  <button key={key} onClick={() => {
                                    setArticlePublisher(key);
                                    setArticlePubType(key === 'springer_lncs' ? 'conference' : 'journal');
                                    setArticleRefStyle('sn-mathphys-num');
                                    setArticleAcmType('sigconf');
                                    setArticleElsevierModel('num');
                                  }} style={{
                                    padding: '6px 12px', fontSize: '11.5px', fontWeight: 700,
                                    background: articlePublisher === key ? '#06b6d418' : 'rgba(255,255,255,0.03)',
                                    border: `1.5px solid ${articlePublisher === key ? '#06b6d4' : 'rgba(255,255,255,0.09)'}`,
                                    borderRadius: '7px', cursor: 'pointer',
                                    color: articlePublisher === key ? '#06b6d4' : 'rgba(255,255,255,0.65)', transition: 'all 0.14s', fontFamily: 'inherit',
                                  }}>{label}</button>
                                ))}
                              </div>
                            </div>

                            {/* Document type — not shown for springer_lncs (always conference/proceedings) */}
                            {articlePublisher !== 'arxiv' && articlePublisher !== 'springer_lncs' && (
                              <div>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '8px' }}>{t.app.articlePicker?.pubType || 'Document type'}</p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {[{ key: 'journal', label: t.app.articlePicker?.journal || 'Journal' }, { key: 'conference', label: t.app.articlePicker?.conference || 'Conference' }].map(({ key, label }) => (
                                    <button key={key} onClick={() => setArticlePubType(key)} style={{
                                      padding: '6px 12px', fontSize: '11.5px', fontWeight: 700,
                                      background: articlePubType === key ? '#06b6d418' : 'rgba(255,255,255,0.03)',
                                      border: `1.5px solid ${articlePubType === key ? '#06b6d4' : 'rgba(255,255,255,0.09)'}`,
                                      borderRadius: '7px', cursor: 'pointer',
                                      color: articlePubType === key ? '#06b6d4' : 'rgba(255,255,255,0.65)', transition: 'all 0.14s', fontFamily: 'inherit',
                                    }}>{label}</button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Springer sn-jnl: reference style picker */}
                            {articlePublisher === 'springer' && articlePubType === 'journal' && (
                              <div>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '8px' }}>Reference style</p>
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  {[
                                    { key: 'sn-mathphys-num', label: 'Math/Phys Num' },
                                    { key: 'sn-mathphys-ay',  label: 'Math/Phys AY' },
                                    { key: 'sn-basic',        label: 'Basic' },
                                    { key: 'sn-nature',       label: 'Nature' },
                                    { key: 'sn-aps',          label: 'APS' },
                                    { key: 'sn-vancouver-num',label: 'Vancouver' },
                                  ].map(({ key, label }) => (
                                    <button key={key} onClick={() => setArticleRefStyle(key)} style={{
                                      padding: '5px 10px', fontSize: '11px', fontWeight: 600,
                                      background: articleRefStyle === key ? '#06b6d418' : 'rgba(255,255,255,0.03)',
                                      border: `1.5px solid ${articleRefStyle === key ? '#06b6d4' : 'rgba(255,255,255,0.09)'}`,
                                      borderRadius: '6px', cursor: 'pointer',
                                      color: articleRefStyle === key ? '#06b6d4' : 'rgba(255,255,255,0.55)', transition: 'all 0.14s', fontFamily: 'inherit',
                                    }}>{label}</button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* ACM: format picker */}
                            {articlePublisher === 'acm' && (
                              <div>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '8px' }}>
                                  {articlePubType === 'conference' ? 'Conference format' : 'Journal format'}
                                </p>
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  {(articlePubType === 'conference'
                                    ? [{ key: 'sigconf', label: 'sigconf' }, { key: 'sigplan', label: 'sigplan' }, { key: 'sigchi', label: 'sigchi' }]
                                    : [{ key: 'acmsmall', label: 'acmsmall' }, { key: 'acmlarge', label: 'acmlarge' }, { key: 'acmtog', label: 'acmtog' }]
                                  ).map(({ key, label }) => (
                                    <button key={key} onClick={() => setArticleAcmType(key)} style={{
                                      padding: '5px 10px', fontSize: '11px', fontWeight: 600,
                                      background: articleAcmType === key ? '#06b6d418' : 'rgba(255,255,255,0.03)',
                                      border: `1.5px solid ${articleAcmType === key ? '#06b6d4' : 'rgba(255,255,255,0.09)'}`,
                                      borderRadius: '6px', cursor: 'pointer',
                                      color: articleAcmType === key ? '#06b6d4' : 'rgba(255,255,255,0.55)', transition: 'all 0.14s', fontFamily: 'inherit',
                                    }}>{label}</button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Elsevier: bibliography model picker */}
                            {articlePublisher === 'elsevier' && (
                              <div>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.52)', marginBottom: '8px' }}>Bibliography model</p>
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                  {[
                                    { key: 'num',       label: 'Numbered' },
                                    { key: 'num-names', label: 'Numbered + Names' },
                                  ].map(({ key, label }) => (
                                    <button key={key} onClick={() => setArticleElsevierModel(key)} style={{
                                      padding: '5px 10px', fontSize: '11px', fontWeight: 600,
                                      background: articleElsevierModel === key ? '#06b6d418' : 'rgba(255,255,255,0.03)',
                                      border: `1.5px solid ${articleElsevierModel === key ? '#06b6d4' : 'rgba(255,255,255,0.09)'}`,
                                      borderRadius: '6px', cursor: 'pointer',
                                      color: articleElsevierModel === key ? '#06b6d4' : 'rgba(255,255,255,0.55)', transition: 'all 0.14s', fontFamily: 'inherit',
                                    }}>{label}</button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Math-only notice for itl */}
                    {selectedMode.id === 'itl' && (
                      <div style={{ background: 'rgba(240,165,0,0.07)', border: '1px solid rgba(240,165,0,0.28)', borderRadius: '9px', padding: '11px 14px', fontSize: '12.5px', color: '#f0a500', lineHeight: 1.6 }}>
                        {T.mathOnlyNotice}
                      </div>
                    )}

                    {/* Upload zone / self-contained cards */}
                    {selectedMode.id === 'merge-word' ? (
                      <MergeWordCard lang={lang} />
                    ) : selectedMode.id === 'merge-pdf' ? (
                      <MergePdfCard lang={lang} />
                    ) : selectedMode.id === 'merge-images' ? (
                      <MergeImagesPdfCard lang={lang} />
                    ) : selectedMode.id === 'pptx-pdf' ? (
                      <PptxToPdfCard lang={lang} />
                    ) : selectedMode.id === 'ptl-tables' ? (
                      <PdfTableBoxCard lang={lang} />
                    ) : selectedMode.id === 'split-pdf' ? (
                      <SplitCard lang={lang} fileType="pdf" />
                    ) : selectedMode.id === 'split-word' ? (
                      <SplitCard lang={lang} fileType="word" />
                    ) : selectedMode.id === 'split-pptx' ? (
                      <SplitCard lang={lang} fileType="pptx" />
                    ) : selectedMode.id === 'compress' ? (
                      <CompressCard lang={lang} />
                    ) : selectedMode.id === 'pdf-rtl-word' ? (
                      <RtlPdfWordCard lang={lang} />
                    ) : (
                      (!['ltw', 'ltw-article'].includes(selectedMode.id) || convDir) && (() => {
                        const rm = getRuntimeMode();
                        return rm ? (
                          <UploadZone acceptedFormats={rm.accept} onFileSelect={(file) => { setSelectedFile(file); setResult(null); }} selectedFile={selectedFile} lang={lang} t={t} />
                        ) : null;
                      })()
                    )}

                    {/* Convert button — hidden for self-contained cards */}
                    {!['merge-word', 'merge-pdf', 'merge-images', 'pptx-pdf', 'ptl-tables', 'split-pdf', 'split-word', 'split-pptx', 'compress', 'humanize', 'pdf-rtl-word'].includes(selectedMode.id) && (
                      <button className="btn-primary" onClick={handleConvert}
                        disabled={!selectedFile || loading || (['ltw', 'ltw-article'].includes(selectedMode.id) && !convDir)}>
                        {loading ? <><span className="spinner" />{T.converting}</> : <>⚡ {T.convertNow}</>}
                      </button>
                    )}

                    {/* Result */}
                    {result && (
                      <div>
                        <ResultPanel result={result} lang={lang} t={t} onClear={() => setResult(null)} />
                      </div>
                    )}
                  </div>

                  {/* ── Right: Description panel ── */}
                  <div className="app-desc-panel" style={{
                    background: 'rgba(10, 12, 32, 0.68)',
                    backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
                    borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)',
                    padding: '24px', display: 'flex', flexDirection: 'row', gap: '0',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.45)', direction: dir, overflowY: 'auto',
                  }}>
                    {/* Left: text content */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '18px' }}>
                      {/* Title */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <div style={{ width: 4, height: 28, background: currentMode.accentColor, borderRadius: '2px', flexShrink: 0 }} />
                          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#f0f0f5', margin: 0 }}>
                            {T.modes?.[currentMode.id]?.title || currentMode.id}
                          </h2>
                        </div>
                        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65, margin: 0, paddingLeft: isRTL ? 0 : '14px', paddingRight: isRTL ? '14px' : 0 }}>
                          {T.modes?.[currentMode.id]?.sub || ''}
                        </p>
                      </div>

                      {/* Feature chips */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                        {currentMode.features.map(f => {
                          const color = CHIP_COLORS[f] || currentMode.accentColor;
                          const label = (CHIP_LABELS[f] || {})[lang] || (CHIP_LABELS[f] || {}).en || f;
                          return (
                            <span key={f} style={{ display: 'inline-block', background: `${color}14`, border: `1px solid ${color}35`, borderRadius: '6px', padding: '3px 9px', fontSize: '11px', fontWeight: 600, color, letterSpacing: '0.1px' }}>
                              {label}
                            </span>
                          );
                        })}
                      </div>

                      {/* Divider */}
                      <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)' }} />

                      {/* Bullet points */}
                      {bullets.length > 0 && (
                        <div>
                          <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                            {lang === 'ar' ? 'المميزات' : lang === 'fa' ? 'ویژگی‌ها' : 'Features'}
                          </p>
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {bullets.map((b, i) => (
                              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', direction: dir, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                                <span style={{ flexShrink: 0, marginTop: '3px', width: 6, height: 6, borderRadius: '50%', background: currentMode.accentColor, display: 'inline-block', boxShadow: `0 0 6px ${currentMode.accentColor}60` }} />
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.88)', lineHeight: 1.6 }}>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Version badge */}
                      <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.42)', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                          Smart Converter · {convDir === 'w2l' && currentMode.id.includes('ltw') ? 'DOCX' : currentMode.inputFmt} → {convDir === 'w2l' && currentMode.id.includes('ltw') ? 'TEX' : currentMode.outputFmt}
                        </span>
                      </div>
                    </div>

                    {/* Right: illustration */}
                    <div style={{ width: '160px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', paddingLeft: isRTL ? 0 : '20px', paddingRight: isRTL ? '20px' : 0 }}>
                      <div style={{
                        width: 100, height: 100, borderRadius: '50%',
                        background: `radial-gradient(circle at 40% 35%, ${currentMode.accentColor}28, transparent 70%)`,
                        boxShadow: `0 0 50px ${currentMode.accentColor}35, 0 0 100px ${currentMode.accentColor}15`,
                        border: `1.5px solid ${currentMode.accentColor}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '38px',
                        userSelect: 'none',
                      }}>
                        {MODE_EMOJI[currentMode.id] || '✦'}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 700, color: currentMode.accentColor, letterSpacing: '1px', opacity: 0.75 }}>
                          {currentMode.inputFmt} → {currentMode.outputFmt}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Welcome screen */
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '24px', textAlign: 'center', padding: '40px', direction: dir }}>
                  <div style={{
                    width: 96, height: 96, borderRadius: '50%',
                    background: 'radial-gradient(circle at 40% 35%, rgba(78,154,241,0.22), transparent 70%)',
                    border: '1.5px solid rgba(78,154,241,0.28)',
                    boxShadow: '0 0 55px rgba(78,154,241,0.18), 0 0 100px rgba(78,154,241,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px',
                  }}>
                    ✦
                  </div>
                  <div>
                    <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'rgba(255,255,255,0.92)', margin: '0 0 10px' }}>
                      {lang === 'ar' ? 'اختر أداة تحويل' : lang === 'fa' ? 'یک ابزار تبدیل انتخاب کنید' : 'Select a conversion tool'}
                    </h2>
                    <p style={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.55)', maxWidth: '300px', lineHeight: 1.65 }}>
                      {lang === 'ar' ? 'اختر من القائمة على اليمين للبدء في تحويل ملفاتك'
                        : lang === 'fa' ? 'از منوی سمت راست برای شروع تبدیل فایل‌های خود انتخاب کنید'
                        : 'Choose a category from the sidebar to start converting your files'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {selectedMode?.id !== 'whiteboard' && <Footer />}
          </main>
        </div>
      </div>


    </div>
  );
}
